import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Client for `controllers/theatreTeamController.js` — the theatre team at note save.
 * Task G3 of `tasks/theatre-booking-and-procedure-lifecycle.md` §6.2.
 *
 * ── WHAT THIS SERVICE DELIBERATELY DOES NOT DO ─────────────────────────────────────
 *
 * It does not fetch the colleague roster, and it contains no name matching. Both live on the
 * server, in `utils/theatreTeamMatcher.js`, for the reason §6.1 spends a section on: the value
 * being decided ends up in `PatientProcedureCode.assistantPracticeNumber`, which reaches the
 * switching house. A wrong start time is a visible, correctable error on a note; a wrong
 * assistant bills under another practitioner's number.
 *
 * So this asks one question and receives finished values. `prefill.practiceNumber` has already
 * been read off the matched `Colleague`, or is EMPTY and flagged. There is no branch anywhere
 * in the client that can produce one from anything else.
 */

/** §6.2's four outcomes, plus NOTHING_FOUND split out of the fourth row (see the matcher). */
export type TheatreTeamOutcome =
  | 'MATCHED_SAME_AS_BOOKING'
  | 'MATCHED_DIFFERS_FROM_BOOKING'
  | 'NAME_EXTRACTED_NO_ROSTER_MATCH'
  | 'AMBIGUOUS'
  | 'NOTHING_FOUND';

export type TheatreTeamBehaviour =
  | 'preselect-silently'
  | 'preselect-with-marker'
  | 'prefill-name-flag-practice-number'
  | 'keep-booking';

/** One role's finished answer: what to write, whether to say so, and why. */
export interface TheatreTeamPrefill {
  role: string;
  outcome: TheatreTeamOutcome;
  behaviour: TheatreTeamBehaviour;
  /** The name as the model heard it. Never a roster name — it was never shown the roster. */
  nameAsSpoken: string | null;
  /** What goes in the field. Already resolved: roster name, spoken name, or the booking's. */
  name: string;
  /** From the matched `Colleague` or empty. NEVER from the transcript. */
  practiceNumber: string;
  /** True when the claim cannot complete on this value — an empty practice number. */
  practiceNumberFlagged: boolean;
  /** True for the §6.2 row-3 case: offer to add this person to the roster. */
  offerAddToRoster: boolean;
  colleagueId: string | null;
  source: 'transcript' | 'booking' | 'none';
  /**
   * The visible "from transcript — booking was {X}" line, or null when there is nothing to
   * say. Null on row 1 (same as booking, pre-selected silently) and on row 4 (the booking
   * stands). Non-null is what stops the transcript winning INVISIBLY.
   */
  marker: string | null;
  reason: string;
}

export interface TheatreTeamBooking {
  colleagueId: string;
  name: string;
  practiceNumber: string | null;
}

export interface TheatreTeamPrefillResponse {
  success: boolean;
  message?: string;
  aiRequestId: string;
  groupId: string;
  patientId: string;
  appointmentId: string | null;
  /** False when nothing was extracted — every consult note, and anything predating G1. */
  detected: boolean;
  booking: { assistant: TheatreTeamBooking | null; anaesthetist: TheatreTeamBooking | null };
  assistant: TheatreTeamPrefill;
  anaesthetist: TheatreTeamPrefill;
  /** Mapped onto `app.data.ts:52 ANESTHESIA`, or null for "we could not tell". */
  anaesthesiaType: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TheatreTeamService {
  private apiUrl = `api/theatre-team`;

  constructor(private http: HttpClient) { }

  private authHeaders() {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * The §6.2 verdict for one AI request, with the booking resolved server-side.
   *
   * `appointmentId` is optional and is only ever a link somebody made on purpose — the server
   * has no date-proximity fallback, because §7 forbids adding a second guessing mechanism to
   * the fragile one it already documents.
   */
  loadNotePrefill(aiRequestId: string, appointmentId?: string): Observable<TheatreTeamPrefillResponse> {
    const query = appointmentId ? `?appointmentId=${encodeURIComponent(appointmentId)}` : '';
    return this.http.get<TheatreTeamPrefillResponse>(
      `${this.apiUrl}/note-prefill/${aiRequestId}${query}`,
      { headers: this.authHeaders() }
    );
  }
}
